// AiModeToggle — header pill for switching between AI 模式 / 專業模式.
//
// Toggle behaviour (Q2-II — hybrid overlay):
//   - Pro → AI: navigate to /dashboard/topology. The toggle click is
//     the operator's explicit「我要進 AI 工作空間」 signal, so we drop
//     them on the chat-as-central-workspace landing.
//   - AI → Pro: stay on current URL. Their page already renders fine
//     in pro mode.
//   - AI's own propose_quick_nav buttons NAVIGATE without touching mode
//     (handled in QuickNavRow); the floating ChatBubble follows them
//     so context isn't lost.

import React from "react";
import { Sparkles, LayoutGrid } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAiMode } from "../AiModeContext.js";

const AI_HOME_ROUTE = "/dashboard/topology";

export default function AiModeToggle(): React.ReactElement {
  const { mode, setMode, error } = useAiMode();
  const navigate = useNavigate();
  const location = useLocation();
  const isAi = mode === "ai";

  const switchToAi = async () => {
    if (isAi) return;
    await setMode("ai");
    if (!location.pathname.startsWith(AI_HOME_ROUTE)) {
      navigate(AI_HOME_ROUTE);
    }
  };

  const switchToPro = async () => {
    if (!isAi) return;
    await setMode("professional");
  };

  return (
    <div
      role="group"
      aria-label="操作模式切換"
      title={error ?? undefined}
      className={`relative inline-flex items-center gap-0.5 rounded-md border bg-bg-inset p-0.5 ${
        error ? "border-danger/60" : "border-border"
      }`}
    >
      <button
        type="button"
        onClick={() => { void switchToAi(); }}
        aria-pressed={isAi}
        aria-label="切換到 AI 模式"
        data-tour-id="ui.mode.ai"
        className={`flex items-center gap-1 h-7 px-2 rounded text-2xs font-medium transition-colors ${
          isAi
            ? "bg-accent text-text-inverse shadow-sm"
            : "text-text-muted hover:text-text-primary hover:bg-bg-hover"
        }`}
      >
        <Sparkles className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">AI 模式</span>
      </button>
      <button
        type="button"
        onClick={() => { void switchToPro(); }}
        aria-pressed={!isAi}
        aria-label="切換到專業模式"
        data-tour-id="ui.mode.professional"
        className={`flex items-center gap-1 h-7 px-2 rounded text-2xs font-medium transition-colors ${
          !isAi
            ? "bg-accent text-text-inverse shadow-sm"
            : "text-text-muted hover:text-text-primary hover:bg-bg-hover"
        }`}
      >
        <LayoutGrid className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">專業模式</span>
      </button>
    </div>
  );
}
